import React, { useState } from 'react';
import { Phone, Mail, User, MessageSquare, Calendar, Instagram, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      const result = await response.json();
      toast.success('Thank you! We\'ll contact you within 24 hours.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        message: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Free Quote
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
            Ready to start your project? Contact us today for a free, no-obligation consultation
          </p>
          <p className="text-lg text-green-600 font-semibold">
            Serving Goole, Howden, Thorne, Selby, York & Surrounding Areas
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="border-0 shadow-xl relative overflow-hidden">
              {/* Logo in corner */}
              <div className="absolute top-4 right-4 opacity-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_6ff89c30-29b2-4ef0-998b-b868478450b8/artifacts/blcaejru_WhatsApp%20Image%202026-02-08%20at%2018.37.39.jpeg"
                  alt="MS Building Contractors"
                  className="w-24 h-24 object-contain"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Contact Information</CardTitle>
                <CardDescription>Get in touch with our directors directly</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {contactInfo.directors.map((director, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{director.name} - {director.role}</div>
                      <a 
                        href={`tel:${director.phone}`} 
                        className="text-green-600 hover:text-green-700 flex items-center mt-1 font-medium"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {director.phone}
                      </a>
                    </div>
                  </div>
                ))}

                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Email Us</div>
                    <a 
                      href={`mailto:${contactInfo.email}`} 
                      className="text-green-600 hover:text-green-700 break-all font-medium"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Book with Us */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-600 to-green-700 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Why Book with Us?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Fast Response</div>
                    <div className="text-green-50">We'll get back to you within 24 hours</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MessageSquare className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Free Consultation</div>
                    <div className="text-green-50">No obligation quote and project advice</div>
                  </div>
                </div>
                
                {/* Social Media Links */}
                {contactInfo.socialMedia && (contactInfo.socialMedia.instagram || contactInfo.socialMedia.facebook) && (
                  <div className="pt-4 border-t border-white/20">
                    <div className="font-semibold mb-3">Follow Our Work</div>
                    <div className="flex space-x-3">
                      {contactInfo.socialMedia.instagram && (
                        <a
                          href={contactInfo.socialMedia.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                          aria-label="Instagram"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {contactInfo.socialMedia.facebook && (
                        <a
                          href={contactInfo.socialMedia.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                          aria-label="Facebook"
                        >
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Booking Form */}
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Request a Quote</CardTitle>
              <CardDescription>Fill in your details and we'll be in touch soon</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="07123 456789"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="projectType">Project Type *</Label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                  >
                    <option value="">Select a service</option>
                    <option value="extension">Extension</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="patio">Patio</option>
                    <option value="joinery">Joinery Work</option>
                    <option value="full-package">Full Package</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message">Project Details *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    required
                    rows={4}
                    className="mt-2"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Request Free Quote'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
